import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import s from '../../pages/PedidosOrcamentos.module.css';

// Seção "TRIBUTAÇÃO MUNICIPAL — ISSQN" antes de emitir a NFS-e, quando a
// organização tem a retenção inteligente ligada (letrotec, 2026-11-04).
// O backend decide (A retém / B não retém / C precisa de análise fiscal);
// C — ou revisão manual de A/B — exige decisão de admin/gerente com
// justificativa, que vai junto no POST /fiscal/nfse (decisao_iss).
//
// onEmitir(extra) é chamado DIRETO no clique (sem await antes): as telas
// abrem a aba do DANFSe com window.open dentro dele, e o navegador só deixa
// abrir pop-up em resposta direta a um clique.

export const AVISO_INCERTEZA = 'Não foi possível confirmar automaticamente a retenção do ISSQN. Verifique a legislação municipal ou solicite validação fiscal.';

const moeda = v => (v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const pct = v => (v == null ? '—' : `${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`);

const ROTULO = {
  A: { txt: 'ISS RETIDO PELO TOMADOR', cor: '#1e40af', fundo: '#dbeafe' },
  B: { txt: 'SEM RETENÇÃO', cor: '#166534', fundo: '#dcfce7' },
  C: { txt: 'NECESSITA ANÁLISE FISCAL', cor: '#92400e', fundo: '#fef3c7' },
};

function Linha({ rotulo, children }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 13, padding: '3px 0', borderBottom: '1px dashed #e5e7eb' }}>
      <span style={{ width: 210, color: '#555', flexShrink: 0 }}>{rotulo}</span>
      <span style={{ fontWeight: 600 }}>{children}</span>
    </div>
  );
}

export default function ModalTributacaoIss({ vendaId, item, onEmitir, onFechar }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [revisar, setRevisar] = useState(false);
  const [retem, setRetem] = useState(null);
  const [justificativa, setJustificativa] = useState('');
  const [lembrar, setLembrar] = useState(true);
  const [senha, setSenha] = useState('');

  useEffect(() => {
    let ativo = true;
    api.post('/fiscal/nfse/analise-iss', { venda_id: vendaId, item_venda_id: item.id })
      .then(({ data }) => { if (ativo) setDados(data); })
      .catch(e => { if (ativo) setErro(e.response?.data?.erro || 'Não foi possível analisar a retenção do ISS.'); });
    return () => { ativo = false; };
  }, [vendaId, item.id]);

  const a = dados?.analise;
  const precisaDecisao = a?.resultado === 'C' || revisar;
  const justOk = justificativa.trim().length >= 10;
  const desligada = dados?.ativo === false;
  const podeEmitir = desligada || (!!a && (!precisaDecisao || (retem !== null && justOk && (dados.pode_decidir || senha))));

  const emitir = () => {
    if (!podeEmitir) return;
    onEmitir(precisaDecisao ? {
      decisao_iss: { retem, justificativa: justificativa.trim(), lembrar_tomador: lembrar, senha_autorizacao: senha || undefined },
    } : {});
  };

  const r = a ? ROTULO[a.resultado] : null;
  const p = a?.previsao || {};

  return (
    <div className={s.overlay} style={{ zIndex: 3000 }} onMouseDown={e => e.target === e.currentTarget && onFechar()}>
      <div className={s.modal} style={{ maxWidth: 620, maxHeight: '92vh' }}>
        <div className={s.modalTitle}>TRIBUTAÇÃO MUNICIPAL — ISSQN</div>
        <div className={s.modalBody} style={{ overflowY: 'auto' }}>
          <div style={{ fontSize: 12, color: '#555', marginBottom: 8 }}>
            {item.descricao}{dados?.tomador_nome ? ` — tomador: ${dados.tomador_nome}` : ''}
          </div>
          {erro && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: 8, borderRadius: 4, fontSize: 13 }}>{erro}</div>}
          {desligada && <div style={{ padding: 12, fontSize: 13 }}>Retenção inteligente desligada — a nota segue a regra do cadastro do cliente.</div>}
          {!a && !erro && !desligada && <div style={{ padding: 20, textAlign: 'center' }}>Consultando parâmetros do município…</div>}
          {a && (
            <>
              <div data-testid="situacao-iss" style={{ background: r.fundo, color: r.cor, padding: '8px 10px', borderRadius: 4, fontWeight: 700, marginBottom: 8 }}>
                {r.txt}{a.resultado !== 'C' && !a.decisaoManual ? ' — identificada automaticamente' : ''}
              </div>
              {a.resultado === 'C' && (
                <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', padding: 8, borderRadius: 4, fontSize: 13, marginBottom: 8 }}>
                  <strong>{AVISO_INCERTEZA}</strong>
                  <div style={{ marginTop: 4 }}>{a.motivo}</div>
                  {a.baseLegal && <div style={{ marginTop: 4, fontSize: 12, color: '#555' }}>Parâmetro oficial do município: {a.baseLegal}</div>}
                </div>
              )}
              <Linha rotulo="Município de incidência">{dados.municipio_nome} ({a.municipioIncidencia})</Linha>
              <Linha rotulo="Código do serviço">{a.codigoServico}{a.subitem ? ` (subitem ${a.subitem})` : ''}</Linha>
              <Linha rotulo="Base de cálculo">{moeda(p.vBC)}</Linha>
              <Linha rotulo="Alíquota do ISS">{pct(a.aliquota ?? a.aliquotaMunicipal)}{a.aliquota == null && a.aliquotaMunicipal != null ? ' (municipal)' : ''}</Linha>
              <Linha rotulo="Valor do ISS">{moeda(p.vIss)}</Linha>
              <Linha rotulo="Situação da retenção">{a.retem === true ? 'Retido pelo tomador' : a.retem === false ? 'Não retido' : 'A definir'}</Linha>
              <Linha rotulo="Responsável pelo recolhimento">{a.responsavel === 'tomador' ? 'Tomador' : a.responsavel === 'prestador' ? 'Prestador' : 'A definir'}</Linha>
              <Linha rotulo="Valor líquido da nota">{moeda(p.vLiq)}</Linha>
              <Linha rotulo="Origem da regra">{a.regraOrigem || a.motivo || '—'}{a.baseLegal && a.resultado !== 'C' ? ` — ${a.baseLegal}` : ''}</Linha>
              {a.aliquotaOrigem && <Linha rotulo="Origem da alíquota">{a.aliquotaOrigem}</Linha>}
              {(a.avisos || []).map(t => (
                <div key={t} style={{ fontSize: 12, color: '#92400e', marginTop: 6 }}>⚠ {t}</div>
              ))}

              {a.resultado !== 'C' && !revisar && (
                <button type="button" className={s.btnErp} style={{ marginTop: 10, fontSize: 12 }} onClick={() => setRevisar(true)}>
                  ✏️ Revisar manualmente (exige admin/gerente e justificativa)
                </button>
              )}

              {precisaDecisao && (
                <fieldset style={{ marginTop: 10, border: '1px solid #ddd', borderRadius: 4, padding: 10 }}>
                  <legend style={{ fontSize: 13, fontWeight: 700 }}>Decisão fiscal</legend>
                  <label style={{ display: 'block', fontSize: 13 }}>
                    <input type="radio" name="retem" checked={retem === true} onChange={() => setRetem(true)} /> Reter o ISS (tomador recolhe)
                  </label>
                  <label style={{ display: 'block', fontSize: 13 }}>
                    <input type="radio" name="retem" checked={retem === false} onChange={() => setRetem(false)} /> Não reter (prestador recolhe)
                  </label>
                  <label style={{ display: 'block', fontSize: 13, marginTop: 6 }}>
                    Justificativa / fundamento (obrigatório)
                    <textarea className={s.inputErp} rows={2} style={{ width: '100%' }} value={justificativa}
                      placeholder="Ex.: tomador é PJ estabelecida em Imperatriz — LC 05/2022, art. 120, I (confirmado com o contador)"
                      onChange={e => setJustificativa(e.target.value)} />
                  </label>
                  <label style={{ display: 'block', fontSize: 13, marginTop: 4 }}>
                    <input type="checkbox" checked={lembrar} onChange={e => setLembrar(e.target.checked)} /> Usar esta decisão nas próximas notas deste tomador
                  </label>
                  {!dados.pode_decidir && (
                    <label style={{ display: 'block', fontSize: 13, marginTop: 6 }}>
                      Senha de administrador ou gerente
                      <input type="password" className={s.inputErp} value={senha} onChange={e => setSenha(e.target.value)} />
                    </label>
                  )}
                </fieldset>
              )}
            </>
          )}
        </div>
        <div className={s.modalFooter}>
          <button className={s.btnErp} onClick={onFechar}>Cancelar</button>
          <button className={s.btnErpPrimario} onClick={emitir} disabled={!podeEmitir}>Emitir NFS-e</button>
        </div>
      </div>
    </div>
  );
}
